import styled, { css } from 'styled-components';
import { CODE_FONT_FAMILY } from '@stylesheets/font';

/**
 * 3-up grid of starter-template cards. Collapses to a single column on
 * narrow viewports (the prototype switches at 880px). Each card is itself a
 * `<button>` — the whole card is the action.
 */
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 2px 0;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

interface OptionCardProps {
  $accentBg: string;
  $accentFg: string;
  $accentBorder: string;
}

/**
 * One starter-template card. The card is a `<button>` so it is keyboard +
 * screen-reader actionable. CSS variables (`--accent-bg/fg/border`) are set
 * per-card so the hover treatment can use the option's accent colour
 * (matches the prototype's `.npm-card` exactly).
 */
export const OptionCard = styled.button<OptionCardProps>`
  --accent-bg: ${({ $accentBg }) => $accentBg};
  --accent-fg: ${({ $accentFg }) => $accentFg};
  --accent-border: ${({ $accentBorder }) => $accentBorder};

  text-align: left;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerLowest ?? '#0E0E14'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#464555'};
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.large ?? '12px'};
  padding: 8px 12px 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition:
    border-color 140ms ease,
    transform 140ms ease,
    background 140ms ease;
  color: inherit;
  font: inherit;

  &:hover:not(:disabled) {
    border-color: var(--accent-border);
    background: color-mix(
      in oklab,
      ${({ theme: { colors = {} } }) =>
        colors.surfaceContainerLowest ?? '#0E0E14'}
        80%,
      var(--accent-bg)
    );
    transform: translateY(-2px);
  }

  &:hover:not(:disabled) .npm-card__cta {
    color: var(--accent-fg);
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

/** Frame for the per-card SVG preview. 104px tall to match the prototype. */
export const CardPreview = styled.div`
  height: 104px;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainer ?? '#1F1F26'};
  border-radius: 8px;
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#464555'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

interface CardTagProps {
  $accentBg: string;
  $accentFg: string;
  $accentBorder: string;
}

export const CardTag = styled.span<CardTagProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: ${({ $accentBg }) => $accentBg};
  color: ${({ $accentFg }) => $accentFg};
  border: 1px solid ${({ $accentBorder }) => $accentBorder};
  font-family: ${CODE_FONT_FAMILY};
  text-transform: uppercase;

  & > svg {
    width: 13px;
    height: 13px;
  }
`;

export const CardTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  letter-spacing: -0.2px;
`;

export const CardBlurb = styled.div`
  font-size: 12.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
  line-height: 1.5;
`;

export const CardBullets = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

interface BulletItemProps {
  $accentFg: string;
}

export const CardBulletItem = styled.li<BulletItemProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};

  & > svg {
    width: 14px;
    height: 14px;
    color: ${({ $accentFg }) => $accentFg};
    flex-shrink: 0;
  }
`;

export const CardCTA = styled.div.attrs({ className: 'npm-card__cta' })`
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
  padding-top: 4px;
  transition: color 140ms ease;

  & > svg {
    width: 15px;
    height: 15px;
  }
`;

/**
 * Spinner that overlays the CTA row of the card the user just clicked.
 * Keeps the card visible (so they can confirm what they picked) while the
 * create request is in flight.
 */
const spin = css`
  @keyframes new-process-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Spinner = styled.span`
  ${spin}
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1.5px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: new-process-spin 700ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/**
 * Footer row — info hint on the left (flex: 1), Cancel button on the right.
 * Matches the prototype's `.npm-modal .modal__foot { justify-content:
 * space-between }`. The shared Modal's Footer is `justify-content: flex-end`,
 * but a `flex: 1` hint child still pushes the Cancel button to the right.
 */
export const FooterHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
  line-height: 1.4;
  flex: 1 1 auto;
  min-width: 0;

  & > svg {
    width: 16px;
    height: 16px;
    color: ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    flex-shrink: 0;
  }
`;

export const FooterSpacer = styled.div`
  flex: 1;
`;

export const NameStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 16px;
`;

export const NameStepLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#C7C4D7'};
`;

export const NameInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainerHigh ?? '#1F2030'};
  border: 1px solid
    ${({ theme: { colors = {} } }) => colors.outlineVariant ?? '#464555'};
  border-radius: 12px;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E4E1ED'};
  font-size: 15px;
  font-weight: 500;
  padding: 12px 16px;
  outline: none;
  transition: border-color 120ms ease;

  &::placeholder {
    color: #676680;
    opacity: 1;
  }

  &:focus {
    border-color: ${({ theme: { colors = {} } }) =>
      colors.primary ?? '#C1C1FF'};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
